import _ from 'lodash';
import {
  ensureJson,
  getProviderFactory,
  IAuthenticationInfo,
  IPreference,
  IPreferenceProvder,
  IPreferences,
  RestClient,
} from 'slipstream-components';
import TimeSpan from 'src/plugins/ui-report/src/pages/ReportDefinition/CreateSecurityReport/LogReport/TimeSpan';
import AppUtil from '../../util/AppUtil';

const LOCAL_UI_PREFERENCE = 'local_ui_preference';
const REMOTE_UI_PREFERENCE = 'ui_preference';

export default class DefaultPreferenceProvider implements IPreferenceProvder {
  private remotePreference: IPreferences = null;
  private localPreference: IPreferences = null;
  private persistPreference = _.debounce(this.processUpdate, 1000, {
    leading: false,
    trailing: true,
  });

  public getPreferences(local = false) {
    // should only happen when refresh. Recover from session storage
    const ctxKey = this.getCtxKey();
    if (local) {
      if (this.localPreference == null) {
        const prefStr = localStorage.getItem(this.getLocalPreferenceKey());
        if (prefStr != null) {
          this.localPreference = JSON.parse(prefStr);
        } else {
          this.localPreference = {};
        }
      }
      let prefs = this.localPreference[ctxKey];
      if (prefs == null) {
        prefs = {};
        this.localPreference[ctxKey] = prefs;
      }
      return prefs;
    } else {
      if (this.remotePreference == null) {
        const prefStr = sessionStorage.getItem(this.getRemotePreferenceKey());
        if (prefStr != null) {
          this.remotePreference = JSON.parse(prefStr);
          this.remotePreference[ctxKey] = this.remotePreference[ctxKey] || {};
        } else {
          this.remotePreference = {};
          this.remotePreference[ctxKey] = {};
        }
      }
      let userPrefs = this.remotePreference[ctxKey];
      if (userPrefs == null) {
        userPrefs = {};
        this.remotePreference[ctxKey] = userPrefs;
      }
      return userPrefs;
    }
  }

  public getPreferenceById(id: string, local = false): IPreference {
    return this.getPreferences(local)[id];
  }
  public getPreferenceByIdAndSubId(id: string, subId: string, local = false) {
    const preferenceItem = this.getPreferenceById(id, local);
    if (preferenceItem) {
      return preferenceItem[subId];
    }
    return null;
  }
  public setPreferenceById(id: string, value: IPreference, local = false) {
    const prefs = this.getPreferences(local);
    prefs[id] = value;
    this.updatePreference(local);
  }
  public setPreferenceByIdAndSubId(id: string, subId: string, value: any, local = false) {
    let preferenceItem: IPreference = this.getPreferenceById(id, local);
    if (preferenceItem == null) {
      preferenceItem = {};
      // this.setPreferenceById(id, preferenceItem, local);
    }
    preferenceItem[subId] = value;
    this.setPreferenceById(id, preferenceItem, local);
  }

  public async fetchPreference(authInfo: IAuthenticationInfo) {
    const restClient = new RestClient();
    const systemId = getProviderFactory().getSystemId();
    const resp = await restClient.get(
      `/slipstream/preferences/${systemId}/user`,
      null,
      this.getHeaders()
    );
    const remotePreference = ensureJson(resp.body) || {};
    const ctxKey = this.getCtxKey();
    this.remotePreference = this.remotePreference || {};
    this.remotePreference[ctxKey] = remotePreference;
    sessionStorage.setItem(this.getRemotePreferenceKey(), JSON.stringify(this.remotePreference));
    return true;
  }

  public clearCache(local: boolean) {
    throw new Error('not implemented');
  }

  private getCtxKey() {
    const userName = AppUtil.context.getUserName();
    const tenantName = AppUtil.context.getTenant();
    const key = userName + '_' + tenantName;
    return key;
  }

  private getLocalPreferenceKey() {
    return getProviderFactory().getSystemId() + '_' + LOCAL_UI_PREFERENCE;
  }

  private getRemotePreferenceKey() {
    return getProviderFactory().getSystemId() + '_' + REMOTE_UI_PREFERENCE;
  }

  private updatePreference(local = false) {
    if (local) {
      localStorage.setItem(this.getLocalPreferenceKey(), JSON.stringify(this.localPreference));
    } else {
      this.persistPreference.call(this);
    }
  }

  private getHeaders() {
    // env injection doesn't work in webpack yet, use the temporary variable to tell for now.
    if ((window as any).__REACT_PERF_DEVTOOL_GLOBAL_STORE__) {
      return {
        current_project: AppUtil.context.getTenant(),
        current_user: AppUtil.context.getUserName(),
      };
    }
    return {};
  }

  private processUpdate() {
    const systemId = getProviderFactory().getSystemId();
    const url = `/slipstream/preferences/${systemId}/user`;
    const remotePreference = this.getPreferences(false);
    sessionStorage.setItem(this.getRemotePreferenceKey(), JSON.stringify(this.remotePreference));
    return new RestClient().post(url, remotePreference, this.getHeaders());
  }
}
